import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TabTrackingService {
  private readonly LOCKED_SUBMISSIONS_KEY = 'locked_submissions';
  private readonly TAB_ID_KEY = 'tabId';
  private tabId: string;

  constructor() {
    this.tabId = this.getTabId();
    this.setupTabCleanup();
  } 

  /**
   * Get or create a unique ID for this browser tab
   */
  private getTabId(): string {
    let tabId = sessionStorage.getItem(this.TAB_ID_KEY);
        if (!tabId) {
      tabId = `tab_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
      sessionStorage.setItem(this.TAB_ID_KEY, tabId);
    }
    
    return tabId;
  }

  /**
   * Setup event listeners for tab cleanup
   */
  private setupTabCleanup(): void {
    window.addEventListener('beforeunload', () => {
      this.cleanupTabLocks();
    });
    
  }

  /**
   * Get the current tab ID
   */
  public getCurrentTabId(): string {
    return this.tabId;
  }

  /**
   * Register that this tab has locked a submission
   * @param submissionId The ID of the submission being locked
   * @param userId The ID of the current user
   */
  public registerLock(submissionId: string, userId: string): void {
    const lockedSubmissionsStr = localStorage.getItem(this.LOCKED_SUBMISSIONS_KEY) || '{}';
    const lockedSubmissions = JSON.parse(lockedSubmissionsStr);
        lockedSubmissions[submissionId] = {
      tabId: this.tabId,
      userId: userId,
      timestamp: new Date().toISOString(),
      lastActive: new Date().toISOString()
    };
    
    // Save back to localStorage
    localStorage.setItem(this.LOCKED_SUBMISSIONS_KEY, JSON.stringify(lockedSubmissions));
  }
  
  /**
   * Update the last active timestamp for a lock
   * @param submissionId The ID of the submission
   */
  public updateLockActivity(submissionId: string): void {
    const lockedSubmissionsStr = localStorage.getItem(this.LOCKED_SUBMISSIONS_KEY) || '{}';
    const lockedSubmissions = JSON.parse(lockedSubmissionsStr);
      if (lockedSubmissions[submissionId] && lockedSubmissions[submissionId].tabId === this.tabId) {
      lockedSubmissions[submissionId].lastActive = new Date().toISOString();
      localStorage.setItem(this.LOCKED_SUBMISSIONS_KEY, JSON.stringify(lockedSubmissions));
    }
  }
  
  /**
   * Release a lock on a submission
   * @param submissionId The ID of the submission to unlock
   */
  public releaseLock(submissionId: string): void {
    // Get existing locked submissions
    const lockedSubmissionsStr = localStorage.getItem(this.LOCKED_SUBMISSIONS_KEY) || '{}';
    const lockedSubmissions = JSON.parse(lockedSubmissionsStr);
    
    // Check if this tab owns the lock
    if (lockedSubmissions[submissionId] && lockedSubmissions[submissionId].tabId === this.tabId) {
      // Remove the lock
      delete lockedSubmissions[submissionId];
      
      // Save back to localStorage
      localStorage.setItem(this.LOCKED_SUBMISSIONS_KEY, JSON.stringify(lockedSubmissions));
    }
  }
  
  /**
   * Check if a submission is locked by the current user in another tab
   * @param submissionId The ID of the submission to check
   * @param userId The ID of the current user
   * @returns Info about the lock if locked, null if not locked
   */
  public isLockedByCurrentUserInAnotherTab(submissionId: string, userId: string): any {
    // Get existing locked submissions
    const lockedSubmissionsStr = localStorage.getItem(this.LOCKED_SUBMISSIONS_KEY) || '{}';
    const lockedSubmissions = JSON.parse(lockedSubmissionsStr);
    
    // Check if this submission is locked
    if (lockedSubmissions[submissionId]) {
      const lock = lockedSubmissions[submissionId];
      
      // Check if locked by current user but in a different tab
      if (lock.userId === userId && lock.tabId !== this.tabId) {
        return {
          tabId: lock.tabId,
          timestamp: lock.timestamp,
          lastActive: lock.lastActive
        };
      }
    }
    
    return null;
  }
  

  public forceReleaseLock(submissionId: string): void {
    const lockedSubmissionsStr = localStorage.getItem(this.LOCKED_SUBMISSIONS_KEY) || '{}';
    const lockedSubmissions = JSON.parse(lockedSubmissionsStr);

    if (lockedSubmissions[submissionId]) {
      delete lockedSubmissions[submissionId];
      localStorage.setItem(this.LOCKED_SUBMISSIONS_KEY, JSON.stringify(lockedSubmissions));
    }
  }

  /**
   * Clean up locks owned by this tab
   */
  public cleanupTabLocks(): void {
    const lockedSubmissionsStr = localStorage.getItem(this.LOCKED_SUBMISSIONS_KEY) || '{}';
    const lockedSubmissions = JSON.parse(lockedSubmissionsStr);
    let isModified = false;
    
    Object.keys(lockedSubmissions).forEach(submissionId => {
      if (lockedSubmissions[submissionId].tabId === this.tabId) {
        delete lockedSubmissions[submissionId];
        isModified = true;
      }
    });
    
    // Save back to localStorage if changes were made
    if (isModified) {
      localStorage.setItem(this.LOCKED_SUBMISSIONS_KEY, JSON.stringify(lockedSubmissions));
    }
  }
  

}